import awsconfig from '../../aws-exports';
import { Box, Typography } from '@mui/material';
import { InputComponent } from '../../components/inputComponents/InputComponent';
import { PrizeImgFileUpload } from '../inputComponents/FileUpload/PrizeFileUpload.component';
import { TooltipText } from '../../components/inputComponents/TooltipText';
import { useState } from 'react';
import { getIn } from 'formik';
import { defaultNotifState } from '../../constants/currency-constants';
import { defaultModalMetadataState } from '../../constants/prize-constants';
import { Notification } from '../../common/Notification/Notification';
import { ToolTipPlacement } from '../../types/componentTypes/tooltip';
import PrizeImageMetadataModal from '../modals/PrizeImageMetadataModal';
import useLanguageSetup from '../../pages/AddPrize/hooks/useLanguageSetupHook'
import { SwitchButton } from '../inputComponents/SwitchComponent';
import { getEnvAndRegion, getPublicImagePath } from '../inputComponents/FileUpload/fileUploadHelpers';
import { updateMetadataParams, updateImgUrls, updateTempParamsImgUrls } from '../../utils/prizeUtils';
import LocalizedQuillInput from '../../common/ReactQuill/LocalizedQuillInput';
interface PrizeLocalizationProps {
    formik: any;
    language: string;
    backstepDone?: boolean;
    isEdit?: boolean;
    languageIndex?: number;
    tabIndex: number;
    defaultLang: string;
    numberOfTabs: number;
    setLanguageIndex?: (value: number) => void;
}

function PrizeLocalization({
    formik,
    language,
    backstepDone,
    isEdit,
    languageIndex,
    tabIndex,
    defaultLang,
    numberOfTabs,
    setLanguageIndex,
}: PrizeLocalizationProps) {
    const [notificationState, setNotificationState] = useState(defaultNotifState);
    const [modalMetadataState, setModalMetadataState] = useState(defaultModalMetadataState);
    const { region, env, isValidRegion } = getEnvAndRegion(awsconfig.aws_user_files_s3_bucket)
    const path = `prizeImages/${formik.values.configuration_id}/${formik.values.prize_id}/`;
    const prizeImagePath = getPublicImagePath(env, isValidRegion, region, path)

    useLanguageSetup(setLanguageIndex, language, formik.values, formik.setFieldValue, defaultLang)

    const handleImgDeletion = (isDefaultLang = false, url: string) => {
    const imgUrlsCopy = [...formik.values.img_url]
        if (isDefaultLang) {
            const usingDefaultImgs = Object.entries(formik.values.tempParams.langUsingDefaultImgs)?.map(([key, val]) => val === true && key)
            const langCodesUseDefaultImgs = [defaultLang, ...usingDefaultImgs]
            updateImgUrls(url, formik, imgUrlsCopy)
            langCodesUseDefaultImgs?.forEach((lang: string) => {
                updateTempParamsImgUrls(formik, lang, url)
                if(formik.values?.images_metadata?.[lang]?.length !== 0) {
                    updateMetadataParams(formik, lang, url)
                }
            })
        } else {
            const updatedTempParamsImgUrl = updateTempParamsImgUrls(formik, language, url)
            updateImgUrls(url, formik, imgUrlsCopy)
            if(formik.values?.images_metadata?.[language]?.length !== 0) {
                updateMetadataParams(formik, language, url)
            }
            if (updatedTempParamsImgUrl?.length === 0) {
                formik.setFieldValue(`tempParams.langUsingDefaultImgs[${language}]`, true)
            }
        }
    }
    const customSwitchAction = () => {
        const imgUrlsCopy = [...formik.values.img_url]
        const langImgsCopy = formik.values.tempParams.img_url?.[language]
        const langMetadataExists = formik.values?.images_metadata?.[language] && true
        if (langImgsCopy && langImgsCopy?.length !== 0) {
            for (const langImgUrl of langImgsCopy) {
                updateImgUrls(langImgUrl, formik, imgUrlsCopy, language)
                updateTempParamsImgUrls(formik, language, langImgUrl)
                if (langMetadataExists) {
                    updateMetadataParams(formik, language, langImgUrl)
                }
            }
        }
    }
    return (
        <Box className='prize-box-content'>
            <Notification
                notificationState={notificationState}
                setNotificationState={setNotificationState}
                messageVisibilityTime={2000}
            />
            <TooltipText
                dataTestId='prize-name-tooltip'
                styleName='prize-tooltip-text'
                text='Prize Name'
                textVariant='body1'
                tooltip="Make sure this prize name doesn't exist already under this promotion to avoid any potential confusion"
                iconSize='medium'
            />
            <InputComponent
                inputClassName='input-space'
                formik={formik}
                inputValue={getIn(formik.values, `name[${languageIndex}][${language}]`)}
                formikLabel={`name[${languageIndex}][${language}]`}
                placeholder='Up to 90 characters'
                errorPath={'name'}
                dataTestId='prize-name-input'
            />
            {tabIndex > 1 && <SwitchButton
                formik={formik}
                formikValue={`tempParams.langUsingDefaultImgs[${language}]`}
                label={'Use images uploaded for default language'}
                disableToggle={false}
                customAction={customSwitchAction}
            />}
            {(tabIndex <= 1 || !formik.values.tempParams.langUsingDefaultImgs?.[language]) && <>
                <TooltipText
                    dataTestId='prize-image-tooltip'
                    styleName='prize-tooltip-text'
                    text='Prize Image'
                    textVariant='body1'
                    tooltip={'The file name will be used as image name. Please use meaningful file names. \
                Recommended image resolution: 567 x 567 px. Allowed image formats: PNG, JPG, SVG'}
                    iconSize='medium'
                    placement={ToolTipPlacement.RightEnd}
                />
                <PrizeImgFileUpload
                    backstepDone={backstepDone}
                    isEdit={isEdit}
                    cachedFiles={formik.values.tempParams.img_url?.[`${language}`]?.map((url: string) => ({ name: url })) || []}
                    uploadCallback={(urls: Array<String>) => {
                        const imgUrls = []
                        for (const url of urls) {
                            imgUrls.push(`${prizeImagePath}${url}`)
                        }
                        formik.setFieldValue('img_url', [...(getIn(formik.values, 'img_url') || []), ...imgUrls])
                        formik.setFieldValue(`tempParams.img_url`, {
                            ...formik.values.tempParams.img_url,
                            [language]: [...(getIn(formik.values, `tempParams.img_url[${language}]`) || []), ...imgUrls]
                        })
                    }}
                    deleteCallback={(url: string) => handleImgDeletion(defaultLang === language, url)}
                    placeholder={'No file chosen'}
                    bucket='public'
                    filePath={`prizeImages/${formik.values.configuration_id}/${formik.values.prize_id}`}
                    maxFiles={5}
                    usePublicDomain={true}
                    customAllowedFileTypes={['image/png', 'image/jpg', 'image/jpeg', 'image/svg+xml']}
                    canEdit={true}
                    setOpenEditModal={setModalMetadataState}
                    setNotificationState={setNotificationState}
                    progressKey={language}
                    filesProcessed={getIn(formik.values, `tempParams.img_url`)}
                    errorText={(getIn(formik.touched, 'img_url') && getIn(formik.errors, 'img_url')) || (getIn(formik.errors, `tempParams.img_url[${defaultLang}]`)) || (getIn(formik.errors, `tempParams.img_url`))}
                />
            </>}
            <PrizeImageMetadataModal
                props={{
                    showModal: modalMetadataState.open,
                    url: modalMetadataState.url,
                    setModalState: setModalMetadataState,
                    formik,
                    language,
                }}
            />

            <Typography variant="body1" gutterBottom align="left">
                                Long Description
            </Typography>
            <LocalizedQuillInput
                key={`desc-${language}`}
                name={`desc[${languageIndex}][${language}]`}
                formik={formik}
                placeholder="Up to 1000 characters"
                showError={true}
                dataTestId="long-description-prize"
                className="quill-localization-input"
                errorPath="desc"
            />

            <Typography variant="body1" gutterBottom align="left">
                Short Description
            </Typography>
            <LocalizedQuillInput
                key={`short_desc-${language}`}
                name={`short_desc[${languageIndex}][${language}]`}
                formik={formik}
                placeholder="Up to 250 characters"
                showError={true}
                dataTestId="short-description-prize"
                className="quill-localization-input"
                errorPath="short_desc"
            />

            <TooltipText
                dataTestId='prize-redemption-instructions-tooltip'
                styleName='prize-tooltip-text'
                text='Prize Redemption Instructions'
                textVariant='body1'
                tooltip='Enter the terms and conditions on where and by when the winner can redeem the prize, if a specific voucher is required, etc.'
                iconSize='medium'
            />
            <LocalizedQuillInput
                key={`redeem_desc-${language}`}
                name={`redeem_desc[${languageIndex}][${language}]`}
                formik={formik}
                placeholder="Up to 1000 characters"
                showError={true}
                dataTestId="redeem-description-prize"
                className="quill-localization-input"
                errorPath="redeem_desc"
            />

            <TooltipText
                dataTestId='redemption-url-tooltip'
                styleName='prize-tooltip-text'
                text='Redemption URL'
                textVariant='body1'
                tooltip='Is there a URL consumer should go to redeem the voucher or an email address to contact?'
                iconSize='medium'
            />
            <InputComponent
                inputClassName='input-space'
                formik={formik}
                inputValue={getIn(formik.values, 'redemption_link')}
                formikLabel={'redemption_link'}
                placeholder='https://example.com or example@mail.com'
            />
        </Box>
    );
}

export { PrizeLocalization };
